import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClockCountdown, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";

export default function LoginPage() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      setDevCode(data.dev_code || null);
      setStep("code");
      toast.success(data.sent ? "Sign-in code sent to your email" : "Email delivery unavailable — dev code shown below");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value) => {
    const c = value || code;
    if (c.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code: c });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.full_name.split(" ")[0]}`);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex flex-col lg:flex-row">
      <div className="hidden lg:flex flex-1 flex-col justify-between p-14">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-indigo-600 flex items-center justify-center">
            <ClockCountdown size={24} weight="bold" className="text-white" />
          </div>
          <span className="font-heading font-semibold text-white text-2xl tracking-tight">Team</span>
        </div>
        <div className="max-w-lg">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-400 mb-4">Enterprise People Operations</p>
          <h1 className="font-heading text-4xl xl:text-5xl font-semibold text-white tracking-tight leading-tight">
            Time off, sick days & payroll — reconciled automatically.
          </h1>
          <p className="text-slate-400 mt-6 text-base leading-relaxed">
            Granular roles, audit-grade change history, doctor's note workflows and AI-assisted pay configuration for every team member.
          </p>
        </div>
        <p className="text-slate-600 text-xs">Passwordless sign-in · Codes delivered by email</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-white lg:rounded-l-3xl">
        <div className="w-full max-w-sm rise-in">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="h-9 w-9 rounded-md bg-indigo-600 flex items-center justify-center">
              <ClockCountdown size={20} weight="bold" className="text-white" />
            </div>
            <span className="font-heading font-semibold text-slate-900 text-xl tracking-tight">Team</span>
          </div>

          {step === "email" ? (
            <form onSubmit={requestCode}>
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Sign in</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-8">Enter your work email and we'll send you a 6-digit sign-in code.</p>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Work email</label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="login-email-input"
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-10 h-11"
                />
              </div>
              <Button data-testid="request-otp-btn" type="submit" disabled={busy || !email}
                className="w-full h-11 mt-6 bg-[#0A0F1C] hover:bg-indigo-600 text-white font-medium transition-colors">
                {busy ? "Sending…" : <>Email me a code <ArrowRight size={16} className="ml-2" /></>}
              </Button>
              <p className="text-xs text-muted-foreground mt-6">Admin: <span className="font-medium">diers.adam@totalqualityinsurance.com</span></p>
            </form>
          ) : (
            <div>
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Check your email</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-8">
                We sent a 6-digit code to <span className="font-medium text-slate-900">{email}</span>. It expires in 10 minutes.
              </p>
              <div className="flex justify-center" data-testid="otp-input-wrapper">
                <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {devCode && (
                <div data-testid="dev-code-hint" className="mt-6 rounded-md border border-dashed border-indigo-300 bg-indigo-50 px-4 py-3 text-center">
                  <p className="text-xs text-indigo-500 uppercase tracking-widest font-semibold">Dev fallback code</p>
                  <p className="font-heading text-xl font-semibold tracking-[0.4em] text-indigo-700 mt-1">{devCode}</p>
                </div>
              )}
              <Button data-testid="verify-otp-btn" onClick={() => verify()} disabled={busy || code.length !== 6}
                className="w-full h-11 mt-6 bg-[#0A0F1C] hover:bg-indigo-600 text-white font-medium transition-colors">
                {busy ? "Verifying…" : "Sign in"}
              </Button>
              <button data-testid="back-to-email-btn" onClick={() => { setStep("email"); setCode(""); }}
                className="w-full text-sm text-muted-foreground hover:text-slate-900 mt-4 transition-colors">
                Use a different email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
